import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { Search, X } from 'lucide-react';
import { listConversations, type Conversation } from '../api/conversations';
import { listConversationTags } from '../api/conversationTags';
import { getApiClient } from '../api/factory';
import { useLocale, useT } from '../i18n';
import { useHotkeys } from '../hooks/useHotkeys';

type StatusFilter = 'open' | 'closed';
// ANX-047 — таб назначения. 'all' — без фильтра; передаём в API
// `assigned_operator_id=me|unassigned` для двух других значений.
type AssignFilter = 'all' | 'me' | 'unassigned';

interface Props {
  /** id текущего открытого диалога — для подсветки активного пункта. */
  activeId?: string;
}

// Левая колонка inbox-layout'а: фильтры open/closed + список диалогов.
// Поднята в `components/`, потому что используется внутри InboxLayout
// и переживает навигацию между диалогами без перемонтирования
// (TanStack Router держит layout-route, ререндерится только Outlet).
export function ConversationsRail({ activeId }: Props) {
  const [status, setStatus] = useState<StatusFilter>('open');
  const [assign, setAssign] = useState<AssignFilter>('all');
  // ANX-043 — фильтр rail'а по tag'у. null = без фильтра.
  const [tagFilter, setTagFilter] = useState<string | null>(null);
  // ANX-062 — фильтр по CSAT-оценке. Применим только к закрытым диалогам.
  const [csatFilter, setCsatFilter] = useState<'all' | 'positive' | 'negative'>('all');
  // ANX-042 — поиск. `searchInput` — текст в инпуте, `query` — debounce'нутая
  // версия, которая летит в API. Debounce 300 мс ровно по acceptance ANX-042.
  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const t = useT();
  const [locale] = useLocale();
  const navigate = useNavigate();

  useEffect(() => {
    const id = window.setTimeout(() => setQuery(searchInput.trim()), 300);
    return () => window.clearTimeout(id);
  }, [searchInput]);

  const { data, isLoading, error } = useQuery({
    // queryKey включает все фильтры — invalidate'ятся независимо;
    // useMessageNotifier (ANX-053) подписан на ['conversations','open']
    // без assign/q/tag-фильтра, что соответствует дефолту 'all' + пустая q + null tag.
    queryKey: ['conversations', status, assign, query, tagFilter, csatFilter],
    queryFn: () =>
      listConversations(getApiClient(), {
        status,
        ...(assign !== 'all' ? { assignedTo: assign } : {}),
        ...(query !== '' ? { q: query } : {}),
        ...(tagFilter ? { tagId: tagFilter } : {}),
        ...(status === 'closed' && csatFilter !== 'all' ? { csat: csatFilter } : {}),
      }),
    refetchInterval: 10_000,
    refetchOnWindowFocus: true,
  });

  // Тags tenant'а для chip-фильтра. Грузятся лениво ленивым кликом по
  // кнопке «Фильтр по тегу» — не дёргаем при mount, чтобы у тенантов без
  // tag-таксономии не было лишних запросов.
  const [tagBarOpen, setTagBarOpen] = useState(false);
  const { data: tenantTags } = useQuery({
    queryKey: ['conversation_tags'] as const,
    queryFn: () => listConversationTags(getApiClient()),
    staleTime: 5 * 60 * 1000,
    enabled: tagBarOpen || tagFilter !== null,
  });

  // ANX-044 — j/k навигация по списку диалогов. Берём список из текущей
  // отрисовки (то же, что показано в rail), индекс ищем по activeId.
  const items = data?.conversations ?? [];
  useHotkeys(
    {
      j: () => {
        if (items.length === 0) return;
        const idx = activeId ? items.findIndex((c) => c.id === activeId) : -1;
        const next = items[Math.min(idx + 1, items.length - 1)];
        if (next && next.id !== activeId) {
          void navigate({ to: '/conversations/$id', params: { id: next.id } });
        }
      },
      k: () => {
        if (items.length === 0) return;
        const idx = activeId ? items.findIndex((c) => c.id === activeId) : 0;
        const prev = items[Math.max(idx - 1, 0)];
        if (prev && prev.id !== activeId) {
          void navigate({ to: '/conversations/$id', params: { id: prev.id } });
        }
      },
      // ANX-042 — cmd/ctrl+K фокусирует поле поиска.
      'mod+k': () => {
        searchRef.current?.focus();
        searchRef.current?.select();
      },
    },
    { allowInEditable: ['mod+k'] },
  );

  return (
    <div className="flex h-full w-80 shrink-0 flex-col border-r border-slate-200 bg-white">
      <div className="flex items-center justify-between px-4 pt-4 pb-2">
        <h2 className="text-sm font-semibold text-slate-900">{t('conversations.title')}</h2>
        <div className="flex gap-1 text-xs">
          {(['open', 'closed'] as StatusFilter[]).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatus(s)}
              className={
                'rounded-md px-2 py-1 ' +
                (s === status
                  ? 'bg-accent-50 text-accent-700 font-medium'
                  : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700')
              }
            >
              {s === 'open' ? t('conversations.filter.open') : t('conversations.filter.closed')}
            </button>
          ))}
        </div>
      </div>
      {/* ANX-062 — фильтр по CSAT-оценке. Только для закрытых диалогов. */}
      {status === 'closed' && (
        <div className="flex gap-1 px-4 pb-2 text-xs">
          {(['all', 'positive', 'negative'] as const).map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCsatFilter(c)}
              className={
                'rounded-md px-2 py-1 ' +
                (c === csatFilter
                  ? 'bg-accent-50 text-accent-700 font-medium'
                  : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700')
              }
            >
              {c === 'all' ? t('conversations.csat.all') : c === 'positive' ? '👍' : '👎'}
            </button>
          ))}
        </div>
      )}
      {/* ANX-042 — поле поиска. Live-debounce 300 мс; cmd/ctrl+K фокусирует. */}
      <div className="px-4 pb-2">
        <label className="relative block">
          <Search
            size={14}
            aria-hidden="true"
            className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            ref={searchRef}
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape' && searchInput !== '') {
                e.stopPropagation();
                setSearchInput('');
              }
            }}
            placeholder={t('conversations.searchPlaceholder')}
            aria-label={t('conversations.searchPlaceholder')}
            className="w-full rounded-md border border-slate-200 bg-white py-1.5 pl-7 pr-7 text-xs text-slate-800 placeholder:text-slate-400 focus:border-accent-400 focus:outline-none focus:ring-1 focus:ring-accent-200"
          />
          {searchInput !== '' && (
            <button
              type="button"
              onClick={() => {
                setSearchInput('');
                searchRef.current?.focus();
              }}
              aria-label={t('conversations.searchClear')}
              className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            >
              <X size={14} aria-hidden="true" />
            </button>
          )}
        </label>
      </div>
      {/* ANX-043 — фильтр по тегам. Кнопка-стрелка раскрывает chip-bar. */}
      <div className="flex flex-col gap-1 px-4 pb-2 text-xs">
        <button
          type="button"
          onClick={() => setTagBarOpen((v) => !v)}
          className="self-start text-[11px] text-slate-500 hover:text-slate-700"
        >
          {tagFilter
            ? '#' + (tenantTags?.find((tg) => tg.id === tagFilter)?.name ?? '…')
            : t('tags.filterTitle')}
        </button>
        {tagBarOpen && tenantTags && tenantTags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            <button
              type="button"
              onClick={() => setTagFilter(null)}
              className={
                'rounded-full border px-2 py-0.5 text-[10px] ' +
                (tagFilter === null
                  ? 'border-slate-900 bg-slate-900 text-white'
                  : 'border-slate-200 text-slate-600 hover:bg-slate-100')
              }
            >
              {t('tags.filterAll')}
            </button>
            {tenantTags.map((tg) => {
              const active = tagFilter === tg.id;
              return (
                <button
                  key={tg.id}
                  type="button"
                  onClick={() => setTagFilter(active ? null : tg.id)}
                  className={
                    'rounded-full border px-2 py-0.5 text-[10px] ' +
                    (active
                      ? 'border-slate-900 bg-slate-900 text-white'
                      : 'border-slate-200 hover:bg-slate-100')
                  }
                  style={
                    active
                      ? undefined
                      : { backgroundColor: tg.color + '22', borderColor: tg.color + '66', color: tg.color }
                  }
                >
                  {tg.name}
                </button>
              );
            })}
          </div>
        )}
      </div>
      {/* ANX-047 — таб назначения. По умолчанию «Все», чтобы не сбивать
          существующий UX; одиночный оператор почти всегда живёт во «Все». */}
      <div className="flex gap-1 px-4 pb-3 text-xs">
        {(['all', 'me', 'unassigned'] as AssignFilter[]).map((a) => (
          <button
            key={a}
            type="button"
            onClick={() => setAssign(a)}
            className={
              'rounded-md px-2 py-1 ' +
              (a === assign
                ? 'bg-slate-900 text-white'
                : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700')
            }
          >
            {a === 'all'
              ? t('conversations.assignFilter.all')
              : a === 'me'
              ? t('conversations.assignFilter.me')
              : t('conversations.assignFilter.unassigned')}
          </button>
        ))}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {isLoading && (
          <div
            aria-label={t('conversations.loadingAria')}
            className="flex flex-col gap-1 px-2 py-2"
          >
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="h-14 animate-pulse rounded-md bg-slate-100"
                aria-hidden="true"
              />
            ))}
          </div>
        )}
        {error !== null && error !== undefined && (
          <p className="px-4 py-3 text-sm text-red-600" role="alert">
            {t('conversations.loadError')}
          </p>
        )}
        {data !== undefined && data.conversations.length === 0 && (
          <p className="px-4 py-3 text-sm text-slate-500">
            {query !== ''
              ? t('conversations.emptySearch')
              : status === 'open'
              ? t('conversations.emptyOpen')
              : t('conversations.emptyClosed')}
          </p>
        )}
        {data && data.conversations.length > 0 && (
          <ul className="flex flex-col gap-0.5 px-2 py-1">
            {data.conversations.map((c) => (
              <li key={c.id}>
                <ConvLink conv={c} active={c.id === activeId} locale={locale} t={t} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function ConvLink({
  conv,
  active,
  locale,
  t,
}: {
  conv: Conversation;
  active: boolean;
  locale: string;
  t: ReturnType<typeof useT>;
}) {
  const base =
    'block rounded-md px-3 py-2 transition-colors ' +
    (active ? 'bg-accent-50' : 'hover:bg-slate-50');
  // ANX-029: предпочитаем имя/email visitor'а UUID-у, если оставлены.
  const visitorLabel =
    conv.visitor?.name?.trim() ||
    conv.visitor?.email?.trim() ||
    shortVisitorId(conv.visitorId);
  return (
    <Link
      to="/conversations/$id"
      params={{ id: conv.id }}
      aria-current={active ? 'page' : undefined}
      className={base}
    >
      <div className="flex items-center gap-2.5">
        <span
          className={
            'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-medium ' +
            (active
              ? 'bg-accent-100 text-accent-700'
              : 'bg-slate-100 text-slate-600')
          }
          aria-hidden="true"
        >
          {initialsFor(visitorLabel)}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <span
              className={
                'truncate text-sm ' +
                (active ? 'text-slate-900 font-medium' : 'text-slate-800')
              }
            >
              {visitorLabel}
            </span>
            <span className="flex shrink-0 items-center gap-1 text-[11px] text-slate-400">
              {conv.csatScore !== undefined && (
                <span
                  title={t('conversations.csat.rated')}
                  aria-label={conv.csatScore === 1 ? t('conversations.csat.positive') : t('conversations.csat.negative')}
                >
                  {conv.csatScore === 1 ? '👍' : '👎'}
                </span>
              )}
              {formatTime(conv.lastMessageAt, locale)}
            </span>
          </div>
          <div className="flex items-center gap-1.5 truncate text-xs text-slate-500">
            <span className="truncate">
              {conv.assignedOperatorId
                ? t('conversations.assignedTo')
                : t('conversations.unassigned')}
            </span>
            {conv.channel === 'telegram' && (
              <span
                className="shrink-0 rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-medium text-sky-700"
                title={t('conversations.channel.telegram')}
              >
                {'Telegram'}
              </span>
            )}
            {conv.reopenedAt && (
              <span
                className="shrink-0 rounded bg-violet-100 px-1.5 py-0.5 text-[10px] font-medium text-violet-700"
                title={t('conversations.reopened')}
              >
                {'↩ ' + t('conversations.reopened')}
              </span>
            )}
            <AwaitingBadge awaitingSince={conv.awaitingSince} t={t} />
          </div>
        </div>
      </div>
    </Link>
  );
}

// ANX-073 — бейдж ожидания ответа оператора. Цвет по времени ожидания
// (точный SLA-порог per-tenant двигает push на бэке; здесь — визуальный
// индикатор «давно без ответа» по разумным бакетам 5/15 мин).
function AwaitingBadge({
  awaitingSince,
  t,
}: {
  awaitingSince?: string;
  t: ReturnType<typeof useT>;
}) {
  if (!awaitingSince) return null;
  const min = Math.floor((Date.now() - Date.parse(awaitingSince)) / 60000);
  if (Number.isNaN(min) || min < 1) return null;
  const cls =
    min >= 15
      ? 'bg-red-100 text-red-700'
      : min >= 5
      ? 'bg-amber-100 text-amber-700'
      : 'bg-slate-100 text-slate-600';
  return (
    <span
      className={'shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium ' + cls}
      title={t('conversations.awaitingReply')}
    >
      {`⏱ ${min}м`}
    </span>
  );
}

function shortVisitorId(id: string): string {
  if (!id) return '';
  // Сохраняем префикс "v-", если он есть — это маркер визитёра.
  if (id.startsWith('v-')) return id.slice(0, 10);
  return id.slice(0, 8);
}

function initialsFor(label: string): string {
  const trimmed = label.replace(/^v-/, '');
  return trimmed.slice(0, 2).toUpperCase();
}

function formatTime(iso: string, locale: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const tag = locale === 'en' ? 'en-US' : 'ru-RU';
  if (sameDay) {
    return d.toLocaleTimeString(tag, { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString(tag, { day: '2-digit', month: '2-digit' });
}
